import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Container,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';

import FolderIcon from '@mui/icons-material/Folder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useLocation, useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionIcon from '@mui/icons-material/Description'; // file icon
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Page from '../../components/Page';
import Label from '../../components/Label';
import { getDataSourceValidate } from '../../store/Slice/DataSource/DataSourceSlice';
import useDataSourceSelector from '../../store/Slice/DataSource/DataSourceSelector';
import PageTitle from '../../components/PageHeading';
import BrudCrumbs from '../../components/BreadCrumbs';
// import { setIsLoading } from 'src/store/Slice/Loader/LoaderSlice';
import { setIsLoading } from '../../store/Slice/Loader/LoaderSlice';
import RevenueService from '../services/RevenueService';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';

const getLabelColor = (s) =>
  ({
    New: 'info',
    Contacted: 'info',
    ERROR: 'error',
    WARNING: 'warning',
    DND: 'warning',
    VALID: 'success',
    INVALID: 'error',
    Submitted: 'success'
  }[s] || 'warning');

const getIconColor = (s) =>
  ({
    'No. of Records': 'info',
    'Valid Records': 'success',
    'Invalid Records': 'error',
    'Phone - FPD': 'warning'
  }[s] || 'warning');

export default function DataValidate() {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { DownloadDataValidateRecord } = RevenueService;

  const dispatch = useDispatch();
  const location = useLocation();
  const { rowId } = location.state || {};
  const navigate = useNavigate();

  const getViewDataSourceListApi = useCallback(() => {
    dispatch(getDataSourceValidate({ rowId }));
  }, [dispatch, rowId]);

  useEffect(() => {
    getViewDataSourceListApi();
  }, [getViewDataSourceListApi]);

  const { DataSourceValidateData } = useDataSourceSelector();
  const { LoginData } = useLoginSelector();

  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const summaryCards = useMemo(
    () => [
      {
        icon: <FolderIcon />,
        value: DataSourceValidateData?.totalRecords,
        label: 'No. of Records'
      },
      {
        icon: <TrendingUpIcon />,
        value: DataSourceValidateData?.validRecords,
        label: 'Valid Records'
      },
      {
        icon: <WarningAmberIcon />,
        value: DataSourceValidateData?.invalidRecords,
        label: 'Invalid Records'
      },
      {
        icon: <AccessTimeIcon />,
        value: DataSourceValidateData?.phoneFpd,
        label: 'Phone - FPD'
      }
    ],
    [DataSourceValidateData]
  );

  const handleSelect = useCallback(
    async (type) => {
      const credential = {
        datasetId: rowId,
        type: type
      };
      try {
        const DownloadvalidateDataResponse = await DownloadDataValidateRecord(
          credential,
          LoginData?.jwt
        );

        const blob = await DownloadvalidateDataResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${type} Records.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('error', error);
      }
      handleClose();
    },
    [rowId, DownloadDataValidateRecord, LoginData?.jwt, handleClose]
  );
  return (
    <Page title="Partner Management System || Data Validate">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Data Validate" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          {/* -------- SUMMARY CARDS -------- */}
          <Grid container spacing={2} mb={3}>
            {summaryCards?.map((card, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderRadius: 2
                  }}
                >
                  <Label
                    style={{ padding: '20px 10px', borderRadius: '12px' }}
                    variant="ghost"
                    color={getIconColor(card.label)}
                  >
                    {card?.icon}
                  </Label>
                  <Box>
                    <Typography fontSize={22} fontWeight={700}>
                      {card?.value}
                    </Typography>
                    <Typography color="text.secondary"> {card?.label}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* -------- TABLE CARD -------- */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography
              style={{ marginTop: '30px' }}
              variant="body1"
              className="headingLineUp headingLineUpOut"
            >
              Data Validation Results
            </Typography>

            <Tooltip title="Download Records" placement="top" arrow>
              <IconButton
                className="squareIconButton"
                size="small"
                sx={{ p: 0.1 }}
                onClick={handleOpen}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              <MenuItem onClick={() => handleSelect('VALID')}>
                <ListItemIcon>
                  <CheckCircleIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Valid Report" />
              </MenuItem>

              <MenuItem onClick={() => handleSelect('INVALID')}>
                <ListItemIcon>
                  <CancelIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Invalid Report" />
              </MenuItem>

              <MenuItem onClick={() => handleSelect('ALL')}>
                <ListItemIcon>
                  <DescriptionIcon color="warning" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="All Reports" />
              </MenuItem>
            </Menu>
          </Box>
          <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f9fc' }}>
                    <TableCell>
                      <b>FIELD NAME</b>
                    </TableCell>
                    <TableCell>
                      <b>STATUS</b>
                    </TableCell>
                    <TableCell>
                      <b>MESSAGE</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {DataSourceValidateData?.validationResults?.map((r) => (
                    <TableRow key={r.fieldName}>
                      <TableCell>{r.fieldName}</TableCell>
                      {/* <TableCell>{statusChip(r.status)}</TableCell> */}
                      <TableCell>
                        <Label variant="ghost" color={getLabelColor(r.status)}>
                          {r.status}
                        </Label>
                      </TableCell>
                      <TableCell>{r.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Grid item xs={12} textAlign="right">
            <Button
              type="submit"
              variant="contained"
              onClick={() => {
                dispatch(setIsLoading({ loading: true }));
                setTimeout(() => {
                  dispatch(setIsLoading({ loading: false }));
                  navigate(`/data/check-bureau`);
                }, 1000);
              }}
              className="mainBtn"
            >
              Proceed to Check Bureau
            </Button>
          </Grid>
        </Box>
      </Container>
    </Page>
  );
}
